import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

const getTheme = (mode: ThemeMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#60A5FA' : '#0F172A',
      light: mode === 'dark' ? '#93C5FD' : '#334155',
      dark: mode === 'dark' ? '#3B82F6' : '#020617',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8B5CF6',
      light: '#A78BFA',
      dark: '#7C3AED',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#06B6D4',
      light: '#22D3EE',
      dark: '#0891B2',
    },
    background: {
      default: mode === 'dark' ? '#0F0F1A' : '#F8FAFC',
      paper: mode === 'dark' ? '#1A1A2E' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#F1F5F9' : '#0F172A',
      secondary: mode === 'dark' ? '#94A3B8' : '#64748B',
    },
    divider: mode === 'dark' ? '#2D2D44' : '#E2E8F0',
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          backgroundColor: mode === 'dark' ? '#0F0F1A' : '#F8FAFC',
          backgroundImage: mode === 'dark' 
            ? 'radial-gradient(ellipse at top, #1a1a3e 0%, #0F0F1A 50%), radial-gradient(ellipse at bottom, #16162a 0%, #0F0F1A 50%)'
            : 'none',
          minHeight: '100vh',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: mode === 'dark' ? '#1A1A2E' : '#F1F5F9',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb': {
          background: mode === 'dark' ? '#3D3D5C' : '#CBD5E1',
          borderRadius: '4px',
          '&:hover': {
            background: mode === 'dark' ? '#4D4D6D' : '#94A3B8',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: '0.9375rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(139, 92, 246, 0.3)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(139, 92, 246, 0.4)',
          },
        },
        containedPrimary: {
          background: mode === 'dark' 
            ? 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)'
            : 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          '&:hover': {
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)'
              : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: mode === 'dark' ? '#8B5CF6' : '#0F172A',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(mode === 'dark' ? '#8B5CF6' : '#0F172A', 0.08),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: mode === 'dark' 
            ? '0px 4px 20px rgba(0, 0, 0, 0.4)'
            : '0px 1px 3px rgba(15, 23, 42, 0.08), 0px 1px 2px rgba(15, 23, 42, 0.06)',
          border: `1px solid ${mode === 'dark' ? '#2D2D44' : '#E2E8F0'}`,
          backgroundColor: mode === 'dark' ? 'rgba(26, 26, 46, 0.8)' : '#FFFFFF',
          backdropFilter: mode === 'dark' ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: mode === 'dark'
              ? '0px 8px 30px rgba(139, 92, 246, 0.2)'
              : '0px 4px 12px rgba(15, 23, 42, 0.1), 0px 2px 4px rgba(15, 23, 42, 0.06)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'dark' ? 'rgba(26, 26, 46, 0.9)' : '#FFFFFF',
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        colorSuccess: {
          backgroundColor: alpha('#10B981', 0.15),
          color: mode === 'dark' ? '#34D399' : '#059669',
        },
        colorError: {
          backgroundColor: alpha('#EF4444', 0.15),
          color: mode === 'dark' ? '#F87171' : '#DC2626',
        },
        colorWarning: {
          backgroundColor: alpha('#F59E0B', 0.15),
          color: mode === 'dark' ? '#FBBF24' : '#D97706',
        },
        colorInfo: {
          backgroundColor: alpha('#8B5CF6', 0.15),
          color: mode === 'dark' ? '#A78BFA' : '#7C3AED',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: mode === 'dark' ? 'rgba(45, 45, 68, 0.5)' : 'transparent',
            '& fieldset': {
              borderColor: mode === 'dark' ? '#3D3D5C' : '#E2E8F0',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? '#8B5CF6' : '#CBD5E1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#8B5CF6',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: mode === 'dark' ? 'rgba(45, 45, 68, 0.5)' : '#F8FAFC',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: mode === 'dark' ? '#94A3B8' : '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: `2px solid ${mode === 'dark' ? '#3D3D5C' : '#E2E8F0'}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'dark' ? '#2D2D44' : '#F1F5F9'}`,
          padding: '16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.08)' : '#F8FAFC',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: '#8B5CF6',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          minHeight: 48,
          padding: '12px 24px',
          '&.Mui-selected': {
            color: '#8B5CF6',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
        },
        standardSuccess: {
          backgroundColor: alpha('#10B981', 0.1),
          borderColor: alpha('#10B981', 0.3),
          color: mode === 'dark' ? '#34D399' : '#065F46',
        },
        standardError: {
          backgroundColor: alpha('#EF4444', 0.1),
          borderColor: alpha('#EF4444', 0.3),
          color: mode === 'dark' ? '#F87171' : '#991B1B',
        },
        standardWarning: {
          backgroundColor: alpha('#F59E0B', 0.1),
          borderColor: alpha('#F59E0B', 0.3),
          color: mode === 'dark' ? '#FBBF24' : '#92400E',
        },
        standardInfo: {
          backgroundColor: alpha('#8B5CF6', 0.1),
          borderColor: alpha('#8B5CF6', 0.3),
          color: mode === 'dark' ? '#A78BFA' : '#5B21B6',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.3)',
          backgroundColor: mode === 'dark' ? '#1A1A2E' : '#FFFFFF',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: mode === 'dark' ? '2px 0px 20px rgba(0, 0, 0, 0.5)' : '2px 0px 8px rgba(15, 23, 42, 0.08)',
          backgroundColor: mode === 'dark' ? 'rgba(26, 26, 46, 0.95)' : '#FFFFFF',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          padding: '10px 16px',
          '&.Mui-selected': {
            backgroundColor: alpha('#8B5CF6', 0.15),
            color: '#8B5CF6',
            '&:hover': {
              backgroundColor: alpha('#8B5CF6', 0.2),
            },
            '& .MuiListItemIcon-root': {
              color: '#8B5CF6',
            },
          },
          '&:hover': {
            backgroundColor: alpha('#8B5CF6', 0.08),
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: mode === 'dark' ? '#94A3B8' : '#64748B',
        },
      },
    },
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved as ThemeMode) || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = getTheme(mode);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
